import React from 'react';
import { Button, Form, Select, Upload, Image } from 'antd';
import { isAccountActive, openNotification } from '../../../utils';
import { apiFetch } from 'services/apiClient';
import { createMeasuringInstrumentInspector, updateMeasuringInstrumentInspector, getMeasuringInstrumentInspector } from '../services/measuring-instrument-inspectorService';
import { listInspectorsAll } from '../../inspectors/services/inspectorService';
import { CreateMeasuringInstrumentInspectorDto } from '../types/MeasuringInstrumentInspector';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import { getMeasuringInstrumentInventory } from '../../measuring-instrument-inventories/services/measuring-instrument-inventoryService';
import { CursorInWindowIcon } from 'hugeicons-react';

type FormValues = CreateMeasuringInstrumentInspectorDto & { photoUrls?: UploadFile[] };

export default function MeasuringInstrumentInspectorForm({
  id,
  onSuccess,
  referenceId,
  existingAssignments = [],
  excludedAccountId,
}: {
  id?: number;
  onSuccess: () => void;
  referenceId?: number;
  existingAssignments?: any[];
  excludedAccountId?: number;
}) {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = React.useState(false);
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [previewIndex, setPreviewIndex] = React.useState(0);
  const [previewSources, setPreviewSources] = React.useState<string[]>([]);
  const [inspectorOptions, setInspectorOptions] = React.useState<any[]>([]);
  const [inventoryOwnerId, setInventoryOwnerId] = React.useState<number | undefined>(undefined);
  const [selectedAccountId, setSelectedAccountId] = React.useState<number | undefined>(undefined);
  const [selectedInspectorOption, setSelectedInspectorOption] = React.useState<any | null>(null);

  const resolveInspectorAccountId = React.useCallback((inspector: any): number | undefined => {
    const accountId = Number(inspector?.account?.id ?? inspector?.id);
    return Number.isNaN(accountId) || accountId <= 0 ? undefined : accountId;
  }, []);

  const effectiveInspectorOptions = React.useMemo(() => {
    const activeOptions = (inspectorOptions || []).filter((inspector) => isAccountActive(inspector));
    if (!id || !selectedAccountId) {
      return activeOptions;
    }
    const exists = activeOptions.some(
      (inspector) => resolveInspectorAccountId(inspector) === selectedAccountId,
    );
    if (exists) {
      return activeOptions;
    }
    const fallback =
      (selectedInspectorOption && resolveInspectorAccountId(selectedInspectorOption) === selectedAccountId
        ? selectedInspectorOption
        : null) ||
      {
        id: selectedAccountId,
        account: {
          id: selectedAccountId,
          firstName: 'Uzman',
          lastName: `#${selectedAccountId}`,
        },
      };
    return [...activeOptions, fallback];
  }, [id, inspectorOptions, resolveInspectorAccountId, selectedAccountId, selectedInspectorOption]);

  const isDuplicateAssignment = (accountId?: number | string | null) => {
    if (!accountId) return false;
     if(inventoryOwnerId === accountId){
      return true;}

  };
  const fetchInventoryOwner = React.useCallback(async () => {
    if (referenceId === undefined) {
      setInventoryOwnerId(undefined);
      return undefined;
    }
    try {
      const res: any = await getMeasuringInstrumentInventory(referenceId);
      const ownerId = res?.account?.id ?? undefined;
      setInventoryOwnerId(ownerId);
      return ownerId;
    } catch {
      setInventoryOwnerId(undefined);
      return undefined;
    }
  }, [referenceId]);
  React.useEffect(() => {
    if (id) {
      getMeasuringInstrumentInspector(id).then(d => {
        const data: any = { ...d };
        const accountSource =
          (d as any)?.account && typeof (d as any).account === 'object'
            ? (d as any).account
            : undefined;
        if (data.account) data.account = typeof data.account === 'object' ? data.account.id : data.account;
        const normalizedSelectedAccountId = Number(data.account);
        setSelectedAccountId(
          Number.isNaN(normalizedSelectedAccountId) || normalizedSelectedAccountId <= 0
            ? undefined
            : normalizedSelectedAccountId,
        );
        setSelectedInspectorOption(
          accountSource
            ? {
              id: accountSource?.id,
              account: accountSource,
            }
            : null,
        );
        if (Array.isArray(data.photoUrls) && data.photoUrls.length) {
          data.photoUrls = data.photoUrls.map((url: string, index: number) => ({
            uid: `${index}`,
            name: url.split('/').pop() || `foto-${index + 1}`,
            status: 'done',
            url
          })) as UploadFile[];
        }
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ photoUrls: [] } as Partial<FormValues>);
      setSelectedAccountId(undefined);
      setSelectedInspectorOption(null);
    }
    // Fetch inspectors and map to account options (account.id as value)
    listInspectorsAll()
      .then((list: any[]) => {
        const opts = Array.isArray(list) ? list : [];
        setInspectorOptions(opts);
      })
      .catch(() => setInspectorOptions([]));
    fetchInventoryOwner();
  }, [id, form, fetchInventoryOwner]);
  const onFinish = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const { photoUrls, ...rest } = values;
      await fetchInventoryOwner();
      if (isDuplicateAssignment(rest.account)) {
        openNotification('error', 'Hata', 'Bu cihaz zaten aynı P.K.M uzmanına atanmıştır.');
        setSubmitting(false);
        return;
      }
      const data: any = { ...rest };
      data.assignmentAt = new Date().toISOString();
      if (data.account) data.account = { id: data.account };
      if (referenceId !== undefined) data.measuringInstrumentInventory = referenceId;

      const files = Array.isArray(photoUrls) ? photoUrls : [];
      const uploadedUrls: string[] = [];
      for (const file of files) {
        if (file?.originFileObj) {
          const fd = new FormData();
          fd.append('code', 'measuringInstrumentInspectorPhoto');
          fd.append('document', file.originFileObj);
          const uploaded = await apiFetch('/documents', { method: 'POST', body: fd });
          uploadedUrls.push(uploaded.documentUrl || uploaded.filePath || uploaded);
        } else if (typeof file?.url === 'string' && file.url) {
          uploadedUrls.push(file.url);
        }
      }
      data.photoUrls = uploadedUrls;

      if (id) {
        await updateMeasuringInstrumentInspector(id, data);
        openNotification('success', 'Başarılı', 'Ölçüm Cihazı P.K.M Uzmanı Güncellendi');
      } else {
        await createMeasuringInstrumentInspector(data);
        openNotification('success', 'Başarılı', 'Ölçüm Cihazı P.K.M Uzmanı Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Ölçüm Cihazı P.K.M Uzmanı işlemi başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreview = async (file: UploadFile) => {
    const fileList = (form.getFieldValue('photoUrls') as UploadFile[]) || [];
    const entries = await Promise.all(
      fileList.map(async (item, idx) => {
        let src = item.url as string | undefined;
        if (!src && item.preview) src = item.preview as string;
        if (!src && item.originFileObj) {
          const preview = await getBase64(item.originFileObj as File);
          item.preview = preview;
          src = preview;
        }
        return { uid: item.uid || String(idx), src };
      })
    );
    const validEntries = entries.filter((entry) => entry.src) as Array<{ uid: string; src: string }>;
    if (!validEntries.length) return;
    const targetUid = file.uid;
    const index = validEntries.findIndex((entry) => entry.uid === targetUid);
    setPreviewSources(validEntries.map((entry) => entry.src));
    setPreviewIndex(index >= 0 ? index : 0);
    setPreviewVisible(true);
  };

  const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;

  const handleBeforeUpload = React.useCallback((file: RcFile) => {
    if (file.size && file.size > MAX_UPLOAD_SIZE) {
      openNotification('error', 'Hata', 'Maksimum 10MB dosya yüklenebilir.');
      return Upload.LIST_IGNORE;
    }
    return false;
  }, []);

  return (
    <>
      
      <Form form={form} onFinish={onFinish} layout='vertical'>
      <Form.Item name='account' label='P.K.M Uzmanı' rules={[{ required: true }]}>
        <Select
          showSearch
          filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
          placeholder='Seçiniz'
          style={{width:'100%'}}
        >
          {effectiveInspectorOptions.map((ins: any) => {
            const acc = ins?.account || {};
            if (excludedAccountId && Number(acc?.id) === Number(excludedAccountId)) {
              return null;
            }
            const fullName = [acc.firstName, acc.lastName].filter(Boolean).join(' ');
            const label = fullName ? `${fullName} — ${acc.email ?? ''}` : (acc.email ?? ins.id);
            return (
              <Select.Option key={acc.id ?? ins.id} value={acc.id}>
                {label}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      <Form.Item
        name='photoUrls'
        label='Cihaz Fotoğrafları'
        valuePropName='fileList'
        getValueFromEvent={e => e && e.fileList}
        rules={[
          { required: true }
      ]}
      >
        <Upload.Dragger
          multiple
          accept='image/*'
          beforeUpload={handleBeforeUpload}
          listType='picture'
          disabled={submitting}
          onPreview={handlePreview}
        >
          <p className='ant-upload-drag-icon'>
            <CursorInWindowIcon size={48} />
          </p>
          <p className='ant-upload-text' style={{ fontSize: 12 }}>Fotoğrafları sürükleyip bırakın veya dosyaları seçin.</p>
          <p style={{ fontSize: 12, marginBottom: 4, color: 'rgba(0, 0, 0, 0.65)' }}>.jpg, .png formatındaki görseller yüklenebilir.</p>
          <p style={{ fontSize: 12, marginBottom: 0, color: 'rgba(0, 0, 0, 0.65)' }}>Maksimum 10MB dosya yüklenebilir.</p>
        </Upload.Dragger>
      </Form.Item>
      <Button htmlType='submit' type='primary' block loading={submitting}>Kaydet</Button>
    </Form>
    <Image.PreviewGroup
      preview={{
        visible: previewVisible,
        current: previewIndex,
        onVisibleChange: (visible) => setPreviewVisible(visible),
        onChange: (current) => setPreviewIndex(current)
      }}
    >
      {previewSources.map((src, idx) => (
        <Image key={`${idx}-${src}`} src={src} alt={`preview-${idx}`} style={{ display: 'none' }} />
      ))}
    </Image.PreviewGroup>
    </>
  );
}

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
